import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const THEME_STORAGE_KEY_V81 = 'alfredapi.theme.v8.1';
const THEME_MODES_V81 = ['light', 'dark', 'system'];
const ThemeContextV81 = createContext(null);

const getSystemThemeV81 = () =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

const readThemeModeV81 = () => {
  try {
    const storedMode = window.localStorage.getItem(THEME_STORAGE_KEY_V81);
    return THEME_MODES_V81.includes(storedMode) ? storedMode : 'system';
  } catch (_) {
    return 'system';
  }
};

const resolveThemeV81 = (mode) =>
  mode === 'system' ? getSystemThemeV81() : mode;

const applyThemeV81 = (mode) => {
  const resolvedTheme = resolveThemeV81(mode);
  const root = document.documentElement;
  root.dataset.theme = resolvedTheme;
  root.dataset.themeMode = mode;
  root.style.colorScheme = resolvedTheme;
  const themeColor = document.querySelector('meta[name=theme-color]');
  if (themeColor) {
    themeColor.setAttribute(
      'content',
      resolvedTheme === 'dark' ? '#191a1d' : '#f7f8fb'
    );
  }
  return resolvedTheme;
};

export const ThemeProviderV81 = ({ children }) => {
  const [themeMode, setThemeModeState] = useState(readThemeModeV81);
  const [resolvedTheme, setResolvedTheme] = useState(() =>
    applyThemeV81(readThemeModeV81())
  );

  const setThemeMode = useCallback((nextMode) => {
    const safeMode = THEME_MODES_V81.includes(nextMode) ? nextMode : 'system';
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY_V81, safeMode);
    } catch (_) {}
    setThemeModeState(safeMode);
  }, []);

  useEffect(() => {
    setResolvedTheme(applyThemeV81(themeMode));
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mediaQuery || themeMode !== 'system') return undefined;

    const handleSystemThemeChange = () => {
      setResolvedTheme(applyThemeV81('system'));
    };
    mediaQuery.addEventListener?.('change', handleSystemThemeChange);
    return () => {
      mediaQuery.removeEventListener?.('change', handleSystemThemeChange);
    };
  }, [themeMode]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== THEME_STORAGE_KEY_V81) return;
      const nextMode = THEME_MODES_V81.includes(event.newValue)
        ? event.newValue
        : 'system';
      setThemeModeState(nextMode);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const value = useMemo(
    () => ({ themeMode, resolvedTheme, setThemeMode }),
    [resolvedTheme, setThemeMode, themeMode]
  );

  return (
    <ThemeContextV81.Provider value={value}>
      {children}
    </ThemeContextV81.Provider>
  );
};

export const useThemeV81 = () => {
  const context = useContext(ThemeContextV81);
  if (!context) {
    throw new Error('useThemeV81 must be used inside ThemeProviderV81');
  }
  return context;
};
